import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Search, ClipboardList, MoreHorizontal, Trash2,
  CheckCircle2, Clock, AlertCircle, XCircle, ChevronRight, LayoutTemplate,
  ArrowLeft, Calendar,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface TaskDefinition {
  title: string
  description?: string
  category?: string
  assignee?: string
  estimatedMinutes?: number
  required: boolean
}

interface ChecklistTask extends TaskDefinition {
  completed: boolean
  completedAt?: string | null
  completedBy?: string | null
  notes?: string
}

interface OnboardingTemplate {
  id: string
  name: string
  description: string | null
  category: string | null
  tasks: TaskDefinition[]
  estimatedDays: number | null
  isActive: boolean
  createdAt: string
}

interface OnboardingChecklist {
  id: string
  templateId: string | null
  clientName: string
  status: string
  assigneeId: string | null
  startDate: string | null
  targetDate: string | null
  completedAt: string | null
  tasks: ChecklistTask[]
  notes: string | null
  progress: number
  createdAt: string
  updatedAt: string
  template?: { name: string } | null
}

interface OnboardingStats {
  totalActive: number
  totalCompleted: number
  totalNotStarted: number
  totalCancelled: number
  averageCompletionDays: number | null
  templateCount: number
}

// ============================================
// Add Template Dialog
// ============================================

function AddTemplateDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('new_client')
  const [estimatedDays, setEstimatedDays] = useState('')
  const [tasks, setTasks] = useState<TaskDefinition[]>([
    { title: '', description: '', required: true },
  ])
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/onboarding/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] })
      toast({ title: 'Template created', description: 'Onboarding template created successfully' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create template', variant: 'destructive' }),
  })

  function resetForm() {
    setName('')
    setDescription('')
    setCategory('new_client')
    setEstimatedDays('')
    setTasks([{ title: '', description: '', required: true }])
  }

  function addTask() {
    setTasks([...tasks, { title: '', description: '', required: true }])
  }

  function updateTask(index: number, field: string, value: unknown) {
    const updated = [...tasks]
    updated[index] = { ...updated[index], [field]: value }
    setTasks(updated)
  }

  function removeTask(index: number) {
    if (tasks.length <= 1) return
    setTasks(tasks.filter((_, i) => i !== index))
  }

  function handleCreate() {
    const validTasks = tasks.filter((t) => t.title.trim())
    if (!name || validTasks.length === 0) return
    createMutation.mutate({
      name,
      description: description || undefined,
      category,
      estimatedDays: estimatedDays ? parseInt(estimatedDays, 10) : undefined,
      tasks: validTasks,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline"><LayoutTemplate className="mr-2 h-4 w-4" /> New Template</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Onboarding Template</DialogTitle>
          <DialogDescription>Define a reusable onboarding checklist template.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New Client Setup" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_client">New Client</SelectItem>
                  <SelectItem value="migration">Migration</SelectItem>
                  <SelectItem value="expansion">Expansion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the onboarding workflow..." />
          </div>
          <div className="space-y-2">
            <Label>Estimated Days</Label>
            <Input type="number" value={estimatedDays} onChange={(e) => setEstimatedDays(e.target.value)} placeholder="14" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Tasks</Label>
              <Button variant="ghost" size="sm" onClick={addTask}><Plus className="mr-1 h-3 w-3" /> Add Task</Button>
            </div>
            <div className="space-y-3">
              {tasks.map((task, i) => (
                <div key={i} className="flex gap-2 items-start border rounded-md p-3">
                  <div className="flex-1 space-y-2">
                    <Input
                      value={task.title}
                      onChange={(e) => updateTask(i, 'title', e.target.value)}
                      placeholder={`Task ${i + 1} title`}
                    />
                    <Input
                      value={task.description || ''}
                      onChange={(e) => updateTask(i, 'description', e.target.value)}
                      placeholder="Task description (optional)"
                      className="text-sm"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeTask(i)}
                    disabled={tasks.length <= 1}
                    className="shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name || !tasks.some((t) => t.title.trim()) || createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Create Checklist Dialog
// ============================================

function CreateChecklistDialog() {
  const [open, setOpen] = useState(false)
  const [clientName, setClientName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [notes, setNotes] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: templateData } = useQuery({
    queryKey: ['onboarding-templates'],
    queryFn: () => apiClient.get<{ templates: OnboardingTemplate[]; pagination: unknown }>('/onboarding/templates', { limit: 100 }),
  })

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/onboarding/checklists', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-checklists'] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-stats'] })
      toast({ title: 'Onboarding started', description: `Checklist created for ${clientName}` })
      setOpen(false)
      setClientName('')
      setTemplateId('')
      setStartDate('')
      setTargetDate('')
      setNotes('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create checklist', variant: 'destructive' }),
  })

  const templates = templateData?.data?.templates || []

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Onboarding</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start Client Onboarding</DialogTitle>
          <DialogDescription>Create a new onboarding checklist for a client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Client Name</Label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Corp" />
          </div>
          <div className="space-y-2">
            <Label>Template (optional)</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Select a template..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional context..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              clientName,
              templateId: templateId && templateId !== 'none' ? templateId : undefined,
              startDate: startDate || undefined,
              targetDate: targetDate || undefined,
              notes: notes || undefined,
            })}
            disabled={!clientName || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Start Onboarding'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Checklist Detail View
// ============================================

function ChecklistDetail({
  checklist,
  onBack,
}: {
  checklist: OnboardingChecklist
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: detailData, isLoading } = useQuery({
    queryKey: ['onboarding-checklist', checklist.id],
    queryFn: () => apiClient.get<OnboardingChecklist>(`/onboarding/checklists/${checklist.id}`),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ taskIndex, completed }: { taskIndex: number; completed: boolean }) =>
      apiClient.patch(`/onboarding/checklists/${checklist.id}/tasks/${taskIndex}`, { completed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-checklist', checklist.id] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-checklists'] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-stats'] })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' }),
  })

  const detail = detailData?.data || checklist
  const tasks = (detail.tasks || []) as ChecklistTask[]

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold tracking-tight">{detail.clientName}</h2>
          <p className="text-muted-foreground">
            {detail.template?.name && `Template: ${detail.template.name}`}
            {detail.template?.name && ' | '}
            Status: {detail.status.replace('_', ' ')}
          </p>
        </div>
        <Badge variant={
          detail.status === 'completed' ? 'default' :
          detail.status === 'in_progress' ? 'secondary' :
          detail.status === 'cancelled' ? 'destructive' : 'outline'
        }>
          {detail.progress}%
        </Badge>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-muted rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all ${
            detail.progress === 100 ? 'bg-green-500' :
            detail.progress > 50 ? 'bg-blue-500' :
            detail.progress > 0 ? 'bg-yellow-500' : 'bg-muted-foreground/20'
          }`}
          style={{ width: `${detail.progress}%` }}
        />
      </div>

      {/* Date info */}
      {(detail.startDate || detail.targetDate) && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          {detail.startDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Start: {new Date(detail.startDate).toLocaleDateString()}
            </div>
          )}
          {detail.targetDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Target: {new Date(detail.targetDate).toLocaleDateString()}
            </div>
          )}
        </div>
      )}

      {/* Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tasks ({tasks.filter((t) => t.completed).length}/{tasks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {tasks.map((task, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  task.completed ? 'bg-muted/50 border-muted' : 'hover:bg-accent/50'
                }`}
              >
                <Checkbox
                  checked={task.completed}
                  onCheckedChange={(checked) =>
                    toggleMutation.mutate({ taskIndex: index, completed: !!checked })
                  }
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                    {task.title}
                  </p>
                  {task.description && (
                    <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                  )}
                  {task.completedAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Completed {new Date(task.completedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {task.category && <Badge variant="outline" className="shrink-0">{task.category}</Badge>}
              </div>
            ))}
            {tasks.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No tasks in this checklist.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {detail.notes && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Notes</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.notes}</p></CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================
// Status Icon Helper
// ============================================

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'in_progress':
      return <Clock className="h-4 w-4 text-blue-500" />
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-red-500" />
    default:
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />
  }
}

// ============================================
// Main Onboarding Page
// ============================================

export function OnboardingPage() {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState('board')
  const [selectedChecklist, setSelectedChecklist] = useState<OnboardingChecklist | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: checklistData, isLoading: checklistsLoading } = useQuery({
    queryKey: ['onboarding-checklists'],
    queryFn: () => apiClient.get<{ checklists: OnboardingChecklist[]; pagination: unknown }>('/onboarding/checklists', { limit: 100 }),
  })

  const { data: templateData, isLoading: templatesLoading } = useQuery({
    queryKey: ['onboarding-templates'],
    queryFn: () => apiClient.get<{ templates: OnboardingTemplate[]; pagination: unknown }>('/onboarding/templates', { limit: 100 }),
  })

  const { data: statsData } = useQuery({
    queryKey: ['onboarding-stats'],
    queryFn: () => apiClient.get<OnboardingStats>('/onboarding/stats'),
  })

  const deleteChecklistMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/onboarding/checklists/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-checklists'] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-stats'] })
      toast({ title: 'Deleted', description: 'Onboarding checklist removed' })
    },
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/onboarding/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] })
      queryClient.invalidateQueries({ queryKey: ['onboarding-stats'] })
      toast({ title: 'Deleted', description: 'Template removed' })
    },
  })

  const checklists = checklistData?.data?.checklists || []
  const templates = templateData?.data?.templates || []
  const stats = statsData?.data

  const filtered = checklists.filter((c) =>
    c.clientName.toLowerCase().includes(search.toLowerCase())
  )

  // Kanban columns
  const notStarted = filtered.filter((c) => c.status === 'not_started')
  const inProgress = filtered.filter((c) => c.status === 'in_progress')
  const completed = filtered.filter((c) => c.status === 'completed')

  // If viewing a specific checklist
  if (selectedChecklist) {
    return (
      <div className="space-y-6">
        <ChecklistDetail
          checklist={selectedChecklist}
          onBack={() => setSelectedChecklist(null)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Client Onboarding</h1>
          <p className="text-muted-foreground">Manage client onboarding workflows and checklists</p>
        </div>
        <div className="flex gap-2">
          <AddTemplateDialog />
          <CreateChecklistDialog />
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{stats?.totalNotStarted ?? 0}</div>
                <p className="text-xs text-muted-foreground">Not Started</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.totalActive ?? 0}</div>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.totalCompleted ?? 0}</div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-2xl font-bold">{stats?.templateCount ?? 0}</div>
                <p className="text-xs text-muted-foreground">Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {stats?.averageCompletionDays !== null && stats?.averageCompletionDays !== undefined && (
        <p className="text-sm text-muted-foreground">
          Average completion time: <span className="font-medium">{stats.averageCompletionDays} days</span>
        </p>
      )}

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search clients..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="board">Kanban Board</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        {/* Kanban Board Tab */}
        <TabsContent value="board">
          {checklistsLoading ? (
            <div className="grid gap-4 md:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-20 w-full" /></CardContent></Card>
              ))}
            </div>
          ) : checklists.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <ClipboardList className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No onboarding checklists yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first onboarding to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Not Started Column */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm">Not Started</h3>
                  <Badge variant="outline" className="ml-auto">{notStarted.length}</Badge>
                </div>
                <div className="space-y-2">
                  {notStarted.map((cl) => (
                    <KanbanCard
                      key={cl.id}
                      checklist={cl}
                      onClick={() => setSelectedChecklist(cl)}
                      onDelete={() => deleteChecklistMutation.mutate(cl.id)}
                    />
                  ))}
                </div>
              </div>

              {/* In Progress Column */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <h3 className="font-semibold text-sm">In Progress</h3>
                  <Badge variant="outline" className="ml-auto">{inProgress.length}</Badge>
                </div>
                <div className="space-y-2">
                  {inProgress.map((cl) => (
                    <KanbanCard
                      key={cl.id}
                      checklist={cl}
                      onClick={() => setSelectedChecklist(cl)}
                      onDelete={() => deleteChecklistMutation.mutate(cl.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Completed Column */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <h3 className="font-semibold text-sm">Completed</h3>
                  <Badge variant="outline" className="ml-auto">{completed.length}</Badge>
                </div>
                <div className="space-y-2">
                  {completed.map((cl) => (
                    <KanbanCard
                      key={cl.id}
                      checklist={cl}
                      onClick={() => setSelectedChecklist(cl)}
                      onDelete={() => deleteChecklistMutation.mutate(cl.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          {templatesLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /></CardContent></Card>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <LayoutTemplate className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No templates yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create a template to streamline onboarding.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => {
                const taskList = template.tasks || []
                return (
                  <Card key={template.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <div className="flex items-center gap-2">
                        <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-base font-medium">{template.name}</CardTitle>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="text-destructive" onClick={() => deleteTemplateMutation.mutate(template.id)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {template.category && <Badge variant="outline">{template.category.replace('_', ' ')}</Badge>}
                        <Badge variant="secondary">{taskList.length} tasks</Badge>
                        {template.estimatedDays && (
                          <Badge variant="secondary">{template.estimatedDays}d</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Created {new Date(template.createdAt).toLocaleDateString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ============================================
// Kanban Card Component
// ============================================

function KanbanCard({
  checklist,
  onClick,
  onDelete,
}: {
  checklist: OnboardingChecklist
  onClick: () => void
  onDelete: () => void
}) {
  const tasks = (checklist.tasks || []) as ChecklistTask[]
  const completedTasks = tasks.filter((t) => t.completed).length

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <StatusIcon status={checklist.status} />
            <span className="font-medium text-sm truncate">{checklist.clientName}</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick(); }}>
                <ChevronRight className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-2 mb-2">
          <div
            className={`h-2 rounded-full transition-all ${
              checklist.progress === 100 ? 'bg-green-500' :
              checklist.progress > 50 ? 'bg-blue-500' :
              checklist.progress > 0 ? 'bg-yellow-500' : 'bg-muted-foreground/20'
            }`}
            style={{ width: `${checklist.progress}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{completedTasks}/{tasks.length} tasks</span>
          <span>{checklist.progress}%</span>
        </div>

        {checklist.template?.name && (
          <div className="mt-2">
            <Badge variant="outline" className="text-xs">{checklist.template.name}</Badge>
          </div>
        )}

        {checklist.targetDate && (
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Due {new Date(checklist.targetDate).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
